import React, { useContext, useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    FormControlLabel,
    TextField,
    FormGroup,
    Checkbox,
    FormControl,
    RadioGroup,
    Radio,
    CardMedia,
} from '@material-ui/core';
import { Link, useHistory } from "react-router-dom";
import { Add as AddIcon } from '@material-ui/icons';
import { ContactsContext } from './ContactsContext';  // Adjust path if necessary
import { CMAContext } from './CMAContext';             // Context containing array of CMAs
import { useReports } from "./ReportContext";          // Context containing array of Reports
import api, { consoleDevLog } from "../apiConfig";
import NumberSelector from './NumberSelector';
import GenerateModal from './Step4/GenerateModal';

// -------------------- PREFERENCES COMPONENT -------------------- //
const Preferences = ({ id }) => {
    const realEstatePreferences = [
        "Detached House",
        "Semi-Detached House",
        "Town House",
        "Condo",
        "Country House",
        "Villa",
        "Bungalow",
        "Farm House",
        "Penthouse",
        "Cottage",
        "Duplex",
        "Triplex",
        "Loft"
    ];

    const [isLoading, setIsLoading] = useState(true);
    const [formState, setFormState] = useState({
        location: null,
        propertyType: [],
        priceRange: null,
        numBedrooms: null,
        numBathrooms: null,
        minSqFootage: null,
        amenities: [],
        neighborhood: null,
        moveInDate: null,
        additionalComments: null
    });

    // Fetch saved preferences from backend for this client
    useEffect(() => {
        const fetchPreferences = async () => {
            try {
                setIsLoading(true);
                const response = await api.get(`/clients/${id}`);
                if (response.data && response.data.preferences) {
                    setFormState(response.data.preferences);
                }
            } catch (error) {
                console.error("An error occurred while fetching the preferences:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPreferences();
    }, [id]);

    // Generic change handler for form fields
    const handlePreferencesChange = (event) => {
        const { name, value, checked, type } = event.target;

        if (type === "checkbox") {
            setFormState((prevState) => {
                const currentValues = prevState[name] || [];
                if (checked) {
                    // If checked, add the value to the array
                    return { ...prevState, [name]: [...currentValues, value] };
                } else {
                    // If unchecked, remove the value
                    return { ...prevState, [name]: currentValues.filter(item => item !== value) };
                }
            });
        } else {
            setFormState(prevState => ({ ...prevState, [name]: value }));
        }
    };

    // Submit updated preferences to backend
    const handlePreferencesSubmit = async () => {
        const preparedData = {
            ...formState,
            numBedrooms: formState.numBedrooms ? parseInt(formState.numBedrooms) : null,
            numBathrooms: formState.numBathrooms ? parseInt(formState.numBathrooms) : null,
            minSqFootage: formState.minSqFootage ? parseInt(formState.minSqFootage) : null
        };
        const finalInput = { "preferences": preparedData };

        try {
            const response = await api.patch(`/clients/${id}`, finalInput);
            consoleDevLog(response.data);
        } catch (error) {
            console.error("An error occurred while submitting the preferences:", error);
        }
    };

    const isPropertyTypeChecked = (type) => {
        return Array.isArray(formState.propertyType) && formState.propertyType.includes(type);
    };

    const isAmenityChecked = (amenity) => {
        return Array.isArray(formState.amenities) && formState.amenities.includes(amenity);
    };

    const bedroomOptions = ['1', '2', '3', '4+'];
    const bathroomOptions = ['1', '2', '3', '4+'];

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ marginTop: 20 }}>
            <Typography variant="h6">Real Estate Preferences:</Typography>
            <FormGroup>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label="Location"
                            type="text"
                            name="location"
                            fullWidth
                            value={formState.location || ''}
                            onChange={handlePreferencesChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1">Property Type:</Typography>
                        <FormGroup row>
                            {realEstatePreferences.map((preference, index) => (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            checked={isPropertyTypeChecked(preference)}
                                            onChange={handlePreferencesChange}
                                            name="propertyType"
                                            value={preference}
                                        />
                                    }
                                    label={preference}
                                />
                            ))}
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Price Range"
                            name="priceRange"
                            type="text"
                            onChange={handlePreferencesChange}
                            value={formState.priceRange || ''}
                            fullWidth
                        />
                    </Grid>

                    <NumberSelector
                        label="Number of Bedrooms"
                        name="numBedrooms"
                        value={formState.numBedrooms || ''}
                        options={bedroomOptions}
                        onChange={handlePreferencesChange}
                    />

                    <NumberSelector
                        label="Number of Bathrooms"
                        name="numBathrooms"
                        value={formState.numBathrooms || ''}
                        options={bathroomOptions}
                        onChange={handlePreferencesChange}
                    />

                    <Grid item xs={12}>
                        <TextField
                            label="Minimum Square Footage"
                            name="minSqFootage"
                            type="text"
                            fullWidth
                            value={formState.minSqFootage || ''}
                            onChange={handlePreferencesChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1">Amenities:</Typography>
                        <FormGroup row>
                            {["Swimming Pool", "Gym/Fitness Center", "Parking Space", "Garden/Yard", "Pet-Friendly"].map((amenity, index) => (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            checked={isAmenityChecked(amenity)}
                                            name="amenities"
                                            value={amenity}
                                            onChange={handlePreferencesChange}
                                        />
                                    }
                                    label={amenity}
                                />
                            ))}
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Preferred Neighborhood"
                            name="neighborhood"
                            type="text"
                            fullWidth
                            value={formState.neighborhood || ''}
                            onChange={handlePreferencesChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Move-in Date"
                            name="moveInDate"
                            type="date"
                            fullWidth
                            value={formState.moveInDate || ''}
                            InputLabelProps={{ shrink: true }}
                            onChange={handlePreferencesChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Additional Comments or Requirements"
                            name="additionalComments"
                            type="text"
                            multiline
                            minRows={4}
                            fullWidth
                            value={formState.additionalComments || ''}
                            onChange={handlePreferencesChange}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" fullWidth onClick={handlePreferencesSubmit}>
                            Save Preferences
                        </Button>
                    </Grid>
                </Grid>
            </FormGroup>
        </div>
    );
};

// -------------------- CMAs COMPONENT -------------------- //
const CMAs = ({ contact, data }) => {
    const [openGenerateModal, setOpenGenerateModal] = useState(false);
    const [selectedCMA, setSelectedCMA] = useState(null);

    const handleExport = (cma) => {
        setSelectedCMA(cma);
        setOpenGenerateModal(true);
    };

    const handleCloseGenerateModal = () => {
        setOpenGenerateModal(false);
        setSelectedCMA(null);
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Grid container spacing={2}>
                {data.map((cma, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={cma.id || index}>
                        <Card className="cma-card">
                            <Grid container spacing={0} style={{ flex: 1, alignItems: 'stretch' }}>
                                <Grid 
                                    item 
                                    xs={5} 
                                    style={{ display: 'flex', flexDirection: 'column' }}
                                >
                                    <CardMedia
                                        component="img"
                                        alt={cma.subjectProperty?.Addr}
                                        image={`https://realtime-home-images.s3.amazonaws.com/${cma.subjectProperty?.Ml_num}/1.jpeg`}
                                        title={cma.subjectProperty?.Addr}
                                        className="cma-image"
                                        style={{ flex: 1 }}
                                    />
                                </Grid>

                                <Grid 
                                    item 
                                    xs={7} 
                                    className="cma-content"
                                    style={{ display: 'flex', flexDirection: 'column' }}
                                >
                                    <Typography variant="h5">{cma.cmaName}</Typography>
                                    <Typography variant="body2">
                                        Address: {cma.subjectProperty?.Addr}
                                    </Typography>
                                    <Typography variant="body2">Client: {cma.client}</Typography>
                                    {cma.priceRange && (
                                        <Typography variant="body2">
                                            Price Range: ${cma.priceRange.lower} - ${cma.priceRange.upper}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container spacing={0} style={{ marginTop: 'auto' }}>
                                <Grid item xs={5}>
                                    <Link
                                        to={{
                                            pathname: '/smart-cma/new-cma',
                                            state: { cmaData: cma },
                                        }}
                                    >
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            fullWidth 
                                            style={{ borderRadius: 0 }}
                                        >
                                            EDIT
                                        </Button>
                                    </Link>
                                </Grid>
                                <Grid item xs={7}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        style={{ borderRadius: 0 }}
                                        onClick={() => handleExport(cma)}
                                    >
                                        EXPORT
                                    </Button>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {selectedCMA && (
                <GenerateModal
                    open={openGenerateModal}
                    onClose={handleCloseGenerateModal}
                    cmaData={selectedCMA}
                    selectedContact={contact}
                    shouldRedirect={false}
                />
            )}
        </div>
    );
};

// -------------------- REPORTS COMPONENT -------------------- //
const Reports = ({ contact }) => {
    const { reports } = useReports();
    const clientFullName = `${contact.firstName} ${contact.lastName}`;
    const clientReports = reports.filter((r) => r.client === clientFullName);

    return (
        <div style={{ marginTop: 20 }}>
            <Grid container spacing={2}>
                {clientReports.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <Card className="cma-card">
                            <Grid container>
                                <Grid item xs={5}>
                                    <CardMedia
                                        component="img"
                                        alt="House Image"
                                        height="140"
                                        image={file.image}
                                        title="House Image"
                                        className="cma-image"
                                    />
                                </Grid>
                                <Grid item xs={7} className="cma-content">
                                    <Typography variant="h5">{file.title}</Typography>
                                    <Typography variant="body2">
                                        Address: {file.home?.address || 'No address'}
                                    </Typography>
                                    <Typography variant="body2">Client: {file.client}</Typography>
                                </Grid>
                            </Grid>
                            <Grid container className="cma-actions">
                                <Grid item xs={5}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className="cma-edit-btn"
                                    >
                                        Edit
                                    </Button>
                                </Grid>
                                <Grid item xs={7}>
                                    <Button variant="contained" className="cma-export-btn">
                                        Export
                                    </Button>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </div>
    );
};

// -------------------- MAIN RESOURCEGRID COMPONENT -------------------- //
const ResourceGrid = ({ title, id, data }) => {
    const history = useHistory();
    const { contacts } = useContext(ContactsContext);
    const [open, setOpen] = useState(false);

    // Find the contact object by matching "id" from the route/props
    const selectedContact = contacts.find(contact => contact.id === id);

    useEffect(() => {
        consoleDevLog("Contacts:", contacts);
    }, [contacts]);

    const handleClickOpen = () => {
        setOpen(!open);
    };

    // Logic for the Add button on CMA/Reports
    const handleAddResource = () => {
        if (title.includes('CMA')) {
            const clientName = `${selectedContact.firstName} ${selectedContact.lastName}`;
            history.push({
                pathname: '/smart-cma/new-cma',
                state: { clientName: clientName }
            });
        } else if (title.includes('Reports')) {
            history.push({
                pathname: '/smart-reports/new-smart-report',
                state: { selectedClient: selectedContact }
            });
        }
    };

    return (
        <Card style={{ margin: 20 }}>
            <CardContent>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">{title}</Typography>
                    {(title.includes('CMA') || title.includes('Reports')) ? (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleAddResource}
                        >
                            Add
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleClickOpen}
                        >
                            {open ? 'Close' : 'Add'}
                        </Button>
                    )}
                </Grid>

                {/* Conditionally render based on whether we have a valid contact */}
                {selectedContact ? (
                    <>
                        {open && title === `${selectedContact.firstName}’s Preferences` && (
                            <Preferences id={id} />
                        )}

                        {/* Pass `data` to CMAs to show the CMAs we've already filtered. */}
                        {title === `${selectedContact.firstName}’s CMAs` && (
                            <CMAs contact={selectedContact} data={data} />
                        )}

                        {title === `${selectedContact.firstName}’s Reports` && (
                            <Reports contact={selectedContact} />
                        )}
                    </>
                ) : (
                    <Typography variant="body1">
                        No contact selected
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default ResourceGrid;

